// INPUT batch codec (§20.3): [type u8][firstTick u32][viewTick u32][count u8][count × 10-byte frame].
// Frame: i8 mx, my, ax, ay, sx, sy (×127), u8 held, u8 cShove|cGrab<<4, u8 cJump|cItem<<4,
// u8 cHonk(3) | emote<<3 (3) | cEmote<<6 (2).
import type { InputFrame } from '../types';
import { BinReader, BinWriter } from './binary';
import { AXIS_SCALE, BinMsg, INPUT_FRAME_BYTES, INPUT_HEADER_BYTES, INPUT_MAX_FRAMES } from './schema';

export interface InputBatch { firstTick: number; viewTick: number; frames: InputFrame[] }

const HELD_MASK = 0x1f;
const MAX_EMOTE = 4;

function writeAxisPair(w: BinWriter, x: number, y: number, normalize: boolean): void {
  const l = Math.hypot(x, y);
  const s = l > 0 && (normalize || l > 1) ? 1 / l : 1;
  w.q8(x * s, AXIS_SCALE);
  w.q8(y * s, AXIS_SCALE);
}

/**
 * Encodes 1–8 frames for consecutive ticks starting at `frames[0].tick`. Move vectors longer than
 * 1 and all non-zero aim/assist vectors are normalized before quantization. Pass a reused writer
 * to avoid allocation; the returned view is valid until that writer is reused.
 */
export function encodeInputBatch(frames: readonly InputFrame[], viewTick: number, out: BinWriter = new BinWriter(INPUT_HEADER_BYTES + INPUT_MAX_FRAMES * INPUT_FRAME_BYTES)): Uint8Array {
  if (frames.length < 1 || frames.length > INPUT_MAX_FRAMES) throw new RangeError(`input batch needs 1..${INPUT_MAX_FRAMES} frames, got ${frames.length}`);
  const firstTick = frames[0].tick;
  out.reset();
  out.u8(BinMsg.INPUT);
  out.u32(firstTick);
  out.u32(viewTick);
  out.u8(frames.length);
  frames.forEach((f, i) => {
    if (f.tick !== firstTick + i) throw new RangeError('input batch frames must have consecutive ticks');
    writeAxisPair(out, f.mx, f.my, false);
    writeAxisPair(out, f.ax, f.ay, true);
    writeAxisPair(out, f.sx, f.sy, true);
    out.u8(f.held & HELD_MASK);
    out.u8((f.cShove & 15) | ((f.cGrab & 15) << 4));
    out.u8((f.cJump & 15) | ((f.cItem & 15) << 4));
    out.u8((f.cHonk & 7) | ((f.emote & 7) << 3) | ((f.cEmote & 3) << 6));
  });
  return out.toBytes();
}

/**
 * Decodes an INPUT batch, or returns null if it is malformed (wrong type, bad count, length
 * mismatch). Frames are sanitized per §20.6: move magnitude > 1 is normalized, emote > 4 becomes
 * 0, unknown held bits are dropped. `viewTick` clamping is the server's job (it needs its tick).
 */
export function decodeInputBatch(data: ArrayBuffer | ArrayBufferView): InputBatch | null {
  const r = new BinReader(data);
  if (r.length < INPUT_HEADER_BYTES || r.u8() !== BinMsg.INPUT) return null;
  const firstTick = r.u32();
  const viewTick = r.u32();
  const count = r.u8();
  if (count < 1 || count > INPUT_MAX_FRAMES || r.remaining !== count * INPUT_FRAME_BYTES) return null;
  const frames: InputFrame[] = [];
  for (let i = 0; i < count; i++) {
    let mx = r.q8(AXIS_SCALE);
    let my = r.q8(AXIS_SCALE);
    const m = Math.hypot(mx, my);
    if (m > 1) {
      mx /= m;
      my /= m;
    }
    const ax = r.q8(AXIS_SCALE);
    const ay = r.q8(AXIS_SCALE);
    const sx = r.q8(AXIS_SCALE);
    const sy = r.q8(AXIS_SCALE);
    const held = r.u8() & HELD_MASK;
    const sg = r.u8();
    const ji = r.u8();
    const he = r.u8();
    const emote = (he >> 3) & 7;
    frames.push({
      tick: firstTick + i, mx, my, ax, ay, sx, sy, held,
      cShove: sg & 15, cGrab: sg >> 4, cJump: ji & 15, cItem: ji >> 4,
      cHonk: he & 7, emote: emote > MAX_EMOTE ? 0 : emote, cEmote: he >> 6,
    });
  }
  return { firstTick, viewTick, frames };
}
